import { computed, reactive, ref } from 'vue';

import { createReconciliationRunner } from '../../application/useCases/createReconciliationRunner';
import { createReconciliationTask } from '../../domain/entities/ReconciliationTask';
import { linkSessionActivityToAppointment, loadPotentialAppointments } from '../../infrastructure/api/appointmentApi';

/**
 * Composable that manages the state and logic for running activity reconciliation.
 * It provides a simple interface for preparing, matching, and committing reconciliation tasks.
 * `@returns` {{
 *  reconciliationTasks: import('vue').Ref<
 *    import('../../domain/entities/ReconciliationTask.types').ReconciliationTask[]
 *  >,
 *  isRunning: import('vue').Ref<boolean>,
 *  isComplete: import('vue').ComputedRef<boolean>,
 *  prepare: (sessionActivities: Object[]) => void,
 *  match: (
 *    settings: import('../../domain/entities/ReconciliationSettings.types').ReconciliationSettings
 *  ) => Promise<void>,
 *  commit: () => Promise<void>,
 *  cancel: () => void,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useActivityReconciliationRunner() {
  const reconciliationTasks = ref([]);
  const isRunning = ref(false);

  // Wire the use case to the concrete infrastructure implementation
  const runner = createReconciliationRunner(loadPotentialAppointments, linkSessionActivityToAppointment);

  /**
   * Prepare reconciliation tasks for eligible activities.
   * Forms are excluded because they cannot be linked to appointments
   * through Activity Reconciliation.
   *
   * @param {Object[]} sessionActivities - The selected session activities.
   * @returns {void}
   */
  function prepare(sessionActivities) {
    const reconciliationActivities = sessionActivities.filter(
      (activity) => activity.type !== 'form',
    );

    const reactiveTasks = reconciliationActivities.map((activity) => (
      reactive(createReconciliationTask(activity))
    ));

    runner.prepare(reactiveTasks);
    reconciliationTasks.value = reactiveTasks;
  }

  /**
   * Match session activities to appointments using the given settings.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @param {import('../../domain/entities/ReconciliationSettings.types').ReconciliationSettings} settings
   * @returns {Promise<void>}
   */
  async function match(settings) {
    if (isRunning.value) {
      return;
    }
    isRunning.value = true;
    try {
      await runner.match(settings);
    } finally {
      isRunning.value = false;
    }
  }

  /**
   * Commit all confirmed task links by persisting them via the API.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @returns {Promise<{linked: number, failed: number, cancelled: number}>}
   */
  async function commit() {
    if (isRunning.value) {
      return { linked: 0, failed: 0, cancelled: 0 };
    }
    isRunning.value = true;
    try {
      return await runner.commit();
    } finally {
      isRunning.value = false;
    }
  }

  /**
   * Cancel the ongoing reconciliation process by aborting the workers. This will cause in-flight
   * API calls to fail and mark remaining tasks as canceled.
   * @returns {void}
   */
  function cancel() {
    runner.cancel();
  }

  const isComplete = computed(
    () => !isRunning.value
      && reconciliationTasks.value.length > 0
      && !reconciliationTasks.value.some((task) => task.isActionable()),
  );

  return { reconciliationTasks, isRunning, isComplete, prepare, match, commit, cancel };
}
